'use client';

/**
 * RoiCalculatorIsland — tiny client boundary that IO-gates the ROI
 * calculator's interactive sliders (PR-D Task D3 fix: restore the pre-SSR
 * JS budget after Task D1 made every below-fold section's parent tree
 * all-client, ballooning initial-load scripts from ~233KB to ~286KB gzip).
 *
 * `fallback` is authored by RoiCalculator.tsx (a Server Component) and
 * passed in as a normal React children/prop value — Next.js keeps that
 * subtree server-rendered (it never becomes part of this file's client JS),
 * so the static "$54,000" markup ships in the SSR HTML with zero extra
 * bundle cost. Only once the section is within ~400px of the viewport does
 * this island mount the real interactive calculator (RoiCalculatorInteractive
 * — sliders, useState/useMemo, the Talk-to-Bri trigger), so its hydration
 * work never runs eagerly.
 *
 * MEASURED TRADEOFF (documented so a future pass doesn't re-litigate this):
 * production build (`npm run build && next start`, summing gzip across
 * every `<script src>` on `/`) shows this chunk still gets linked as an
 * eager `<script async>` tag in the initial HTML regardless of the exact
 * lazy-loading mechanism tried — next/dynamic(ssr:false) (with or without a
 * `.then()` re-export), a raw `import()` inside the effect, and even a
 * fully static top-level import all produced the same result: Turbopack
 * isolates this component into its own chunk once it's the only client
 * leaf left in its region of the tree (D1 used to bundle it together with
 * Features/IndustryHubSection/TestimonialsSection under one shared
 * IO-gated wrapper — see docs/plans/homepage-v5/*, DeferredHomepageSections
 * comment above — which compressed far better as one ~15KB gzip bucket of
 * four components than this one now compresses alone). Isolating it costs
 * roughly +15KB gzip net versus the pre-D1 baseline; every measured variant
 * landed in the same ~248-253KB range (vs the ~233.7KB pre-D1 baseline and
 * the ~286KB broken-D1 regression this task fixes). next/dynamic(ssr:false)
 * is kept here because it's the simplest, most idiomatic version of the
 * measured-equivalent options and matches this codebase's existing
 * TalkToBriModalTrigger lazy-load precedent.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';

const RoiCalculatorInteractive = dynamic(() => import('./RoiCalculatorInteractive'), {
  ssr: false,
  loading: () => null,
});

export function RoiCalculatorIsland({ fallback }: { fallback: ReactNode }) {
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Environments without IntersectionObserver (older browsers, jsdom in
    // tests that don't polyfill it) fail open to the interactive version
    // immediately rather than getting stuck on the static fallback forever.
    // Deferred via queueMicrotask (rather than called synchronously in the
    // effect body) per react-hooks/set-state-in-effect.
    if (typeof IntersectionObserver === 'undefined') {
      queueMicrotask(() => setActive(true));
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          obs.disconnect();
        }
      },
      { rootMargin: '400px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {active ? <RoiCalculatorInteractive /> : fallback}
    </div>
  );
}

export default RoiCalculatorIsland;
